import {
  ManuscriptOwnership,
  manuscriptTarget,
  libraryScopes,
} from "./library-ownership";
import {
  RefineBatchUpload,
  UploadChannels,
  type UploadChannel,
  type RefineUploadProvider,
  type RefineUploadExample,
} from "./library-refine-upload";
import { ReviewSummary, ReviewNotices } from "./manuscript-review";
import { LibraryExplorer, type LibraryExplorerProps } from "./library-explorer";
import { LibraryFolderTree } from "./library-tree";
export { LibraryFolderTree } from "./library-tree";
import { AiModelSelect } from "./llm-model-select";
import {
  ExecutionAvailabilityNotice,
  executionAvailabilityLabel,
  useExecutionAvailability,
} from "./execution-availability";
import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import {
  Archive,
  Check,
  ChevronRight,
  FileText,
  Folder,
  LockKeyhole,
  Send,
  Upload,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  Field,
  SegmentedControl,
  Select,
} from "../../design-system";
import {
  LibraryEditor,
  LibraryPreview,
  LibraryMedia,
  useManuscriptCheck,
} from "./library-media";
import {
  useLibraryResource,
  useLibraryList,
  invalidateLibrary,
  libraryError,
} from "./library-runtime";
import type {
  LibraryAdapter,
  LibraryItem,
  FileRef,
  SendResult,
  BatchResult,
  ManuscriptCheck,
} from "./library-adapter";
export type {
  LibraryAdapter,
  LibraryItem,
  LibraryDetail,
  FileRef,
  LibraryRevisionSummary,
  ManuscriptCheck,
} from "./library-adapter";
import {
  libraryStatus,
  useLibraryAdapter,
  type LibraryExample,
} from "./manuscript-library-data";
import "./manuscript-library.css";
const nameOf = (provider: LibraryAdapter, id: string) =>
  provider.customers.find((c) => c.id === id)?.name ?? id;
function Status({ item }: { item: LibraryItem }) {
  return (
    <Badge
      tone={
        item.status === "ready"
          ? "teal"
          : item.status === "draft"
            ? "amber"
            : item.status === "scheduled"
              ? "blue"
              : "neutral"
      }
    >
      {item.locked && <LockKeyhole size={11} />} {libraryStatus[item.status]}
    </Badge>
  );
}
export function LibrarySendDialog({
  open,
  onClose,
  initialIds = [],
  initialCustomer,
  initialExecution,
  onExecutionChange,
  onDirect,
  initialStep = 0,
  provider,
}: {
  open: boolean;
  onClose: () => void;
  initialIds?: string[];
  initialCustomer?: string;
  /** Initial execution customer; independent from the selected manuscript folder. */
  initialExecution?: string;
  onExecutionChange?: (customerId: string) => void;
  onDirect?: () => void;
  initialStep?: number;
  provider?: LibraryAdapter;
}) {
  const adapter = useLibraryAdapter(provider),
    customers = adapter.customers,
    customerName = (id: string) => nameOf(adapter, id);
  const initial =
    initialCustomer ?? adapter.defaultScopeId ?? customers[0]?.id ?? "";
  const scopes = libraryScopes(adapter);
  const [targetOverride, setTargetOverride] = useState<{
    key: string;
    value: string;
  }>();
  const [location, setLocation] = useState(initial + ":all"),
    [deep, setDeep] = useState(true),
    [ids, setIds] = useState(initialIds),
    [step, setStep] = useState(initialStep);
  const [execution, setExecution] = useState(
      initialExecution ?? customers[0]?.id ?? "",
    ),
    [llmModel, setLlmModel] = useState(""),
    [machine, setMachine] = useState("auto"),
    [device, setDevice] = useState("auto");
  const [when, setWhen] = useState("now"),
    [start, setStart] = useState("2026-10-21T09:00"),
    [interval, setInterval] = useState(120);
  const [keywords, setKeywords] = useState<Record<string, string[]>>({}),
    [results, setResults] = useState<SendResult[] | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [owner, folder] = location.split(":");
  const listing = useLibraryList(adapter, {
    customerId: owner,
    folderId: folder,
    deep,
    status: "ready",
  });
  const eligible = listing.items.filter((i) => i.valid && !i.locked);
  const chosen = useLibraryResource(
    adapter,
    JSON.stringify(["selected", ids]),
    () => Promise.all(ids.map((id) => adapter.detail(id))),
  );
  const selected = (chosen.data ?? []).sort((a, b) =>
    a.title.localeCompare(b.title, "ko"),
  );
  const selectionKey = selected.map((i) => i.id).join("|");
  const targets = [...new Set(selected.map(manuscriptTarget))];
  const mixedTargets = targets.length > 1;
  const target =
    targetOverride?.key === selectionKey
      ? targetOverride.value
      : (targets[0] ?? "");
  const fixedTarget = selected.some((i) => !!i.ownerCustomerId);
  const missingTarget = mixedTargets
    ? selected.some((i) => !manuscriptTarget(i))
    : !target;
  const availability = useExecutionAvailability(adapter);
  const executionAvailability = availability.data?.[execution];
  const machineData = {
    data: executionAvailability?.machines,
    loading: !executionAvailability,
  };
  const deviceData = useLibraryResource(
    adapter,
    "devices:" + execution + ":" + machine,
    () => adapter.devices(execution, machine === "auto" ? undefined : machine),
  );
  const modelsData = useLibraryResource(adapter, "llm-models", () =>
    adapter.models ? adapter.models() : Promise.resolve(null),
  );
  const submit = async () => {
    if (busy) return;
    if (missingTarget) {
      setError("대상 고객사를 고르세요.");
      return;
    }
    if (
      when === "later" &&
      (!start ||
        !Number.isFinite(interval) ||
        interval < 1 ||
        !Number.isFinite(new Date(start + "+09:00").getTime()))
    ) {
      setError("시작 시각과 1분 이상의 간격을 입력해 주세요.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (llmModel && !modelsData.data?.allowed.includes(llmModel))
        throw new Error(
          "선택한 AI 모델을 더 이상 사용할 수 없습니다. 다시 선택해 주세요.",
        );
      const value = await adapter.send({
        ids: selected.map((i) => i.id),
        ...(mixedTargets
          ? {
              targetCustomerIds: Object.fromEntries(
                selected.map((i) => [i.id, manuscriptTarget(i)!]),
              ),
            }
          : { targetCustomerId: target }),
        executeCustomerId: execution,
        llmModel: llmModel || undefined,
        machineId: machine === "auto" ? undefined : machine,
        deviceSerial: device === "auto" ? undefined : device,
        keywords: Object.fromEntries(
          selected.map((i) => [i.id, keywords[i.id] ?? i.keywords]),
        ),
        startAt:
          when === "later"
            ? new Date(start + "+09:00").toISOString()
            : undefined,
        intervalMinutes: when === "later" ? interval : undefined,
      });
      setResults(value);
      setStep(2);
      invalidateLibrary(adapter);
    } catch (e) {
      setError(libraryError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
      title="작업 등록 · 원고함에서 선택"
      description="원고의 대상은 보내기 기본값입니다. 원고마다 작업이 하나씩 만들어집니다."
      size="wide"
      footer={
        <DialogActions
          secondary={{
            label: results ? "닫기" : step === 0 ? "취소" : "이전",
            disabled: busy,
            onClick: results
              ? onClose
              : step === 0
                ? onClose
                : () => setStep(0),
          }}
          primary={
            !results
              ? {
                  label:
                    step === 0
                      ? "실행 설정으로"
                      : selected.length + "편 작업 등록",
                  disabled:
                    selected.length === 0 ||
                    busy ||
                    chosen.loading ||
                    !!chosen.error,
                  onClick: () => (step === 0 ? setStep(1) : submit()),
                }
              : undefined
          }
        />
      }
    >
      <div className="ml-send">
        {[
          listing.error,
          chosen.error,
          deviceData.error,
          modelsData.error,
          error,
        ]
          .filter(Boolean)
          .map((message, i) => (
            <p key={i} role="alert" className="ml-error">
              {message}
            </p>
          ))}
        {(listing.error ||
          chosen.error ||
          deviceData.error ||
          modelsData.error) && (
          <Button
            variant="ghost"
            onClick={() => {
              listing.retry();
              chosen.retry();
              availability.retry();
              deviceData.retry();
              modelsData.retry();
            }}
          >
            다시 읽기
          </Button>
        )}
        <div className="ml-steps">
          <b className={step === 0 ? "active" : ""}>1 원고 선택</b>
          <ChevronRight size={13} />
          <b className={step === 1 ? "active" : ""}>2 실행 설정</b>
          <ChevronRight size={13} />
          <b className={step === 2 ? "active" : ""}>3 등록 결과</b>
        </div>
        {onDirect && step === 0 && (
          <SegmentedControl
            label="원고 입력 방법"
            value="library"
            onChange={(v) => {
              if (v === "direct") onDirect();
            }}
            options={[
              { value: "direct", label: "직접 입력" },
              { value: "library", label: "원고함에서 선택" },
            ]}
          />
        )}
        {step === 0 && (
          <>
            <Select
              label="원고함 위치"
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setIds([]);
              }}
            >
              {scopes.flatMap((c) => [
                <option value={c.id + ":all"} key={c.id}>
                  {c.name} / 모든 원고
                </option>,
                <option value={c.id + ":none"} key={c.id + "none"}>
                  {c.name} / 폴더 밖 원고
                </option>,
              ])}
            </Select>
            <div className="ml-send-folder">
              <LibraryFolderTree
                provider={adapter}
                customerId={owner}
                value={folder}
                onSelect={(value) => {
                  setLocation(owner + ":" + value);
                  setIds([]);
                }}
              />
            </div>
            <Checkbox
              label="하위 폴더 포함"
              checked={deep}
              onChange={(v) => {
                setDeep(v);
                setIds([]);
              }}
            />
            <div className="ml-select-summary">
              <strong>
                {customerName(owner)} · 준비됨 {listing.total}편
              </strong>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setIds(
                    ids.length === eligible.length
                      ? []
                      : eligible.map((i) => i.id),
                  )
                }
              >
                {ids.length === eligible.length ? "선택 해제" : "모두 선택"}
              </Button>
            </div>
            <div className="ml-send-list">
              {eligible.map((i) => (
                <label key={i.id}>
                  <input
                    type="checkbox"
                    checked={ids.includes(i.id)}
                    onChange={() =>
                      setIds((v) =>
                        v.includes(i.id)
                          ? v.filter((x) => x !== i.id)
                          : [...v, i.id],
                      )
                    }
                  />
                  <FileText size={17} />
                  <span>
                    <b>{i.title}</b>
                    <small>
                      v{i.revision} · {i.fileCount}개 파일 ·{" "}
                      {i.keywords.join(", ")}
                    </small>
                  </span>
                  <ReviewSummary
                    errors={i.errors}
                    warnings={i.warnings}
                    notices={i.notices?.length}
                  />
                </label>
              ))}
            </div>
            {listing.items.length < listing.total && (
              <Button
                variant="secondary"
                disabled={listing.loading}
                onClick={() => void listing.loadMore()}
              >
                원고 더 보기
              </Button>
            )}
            {!listing.loading && !eligible.length && (
              <p className="ml-empty">
                이 폴더에 보낼 수 있는 준비됨 원고가 없습니다.
              </p>
            )}
            <p className="ml-caption">
              이름순으로 발행합니다. 형식 오류·발행 예정·게시됨 원고는 선택할 수
              없습니다. 중복은 등록 직전에 다시 확인합니다.
            </p>
          </>
        )}
        {step === 1 && (
          <>
            {selected
              .filter((i) => i.notices?.length)
              .map((i) => (
                <details key={i.id}>
                  <summary>
                    {i.title} · 검토 {i.notices!.length}개
                  </summary>
                  <ReviewNotices notices={i.notices!} />
                </details>
              ))}
            <div className="ml-owner">
              <Folder size={17} />
              <b>
                {mixedTargets
                  ? "원고별 대상을 따릅니다"
                  : "대상 · " + (target ? customerName(target) : "선택 안 됨")}
              </b>
              <span>
                {selected.length}편 · 대상을 바꿔도 원고 소유는 그대로입니다
              </span>
            </div>
            {mixedTargets ? (
              <ul className="ml-send-targets">
                {selected.map((i) => (
                  <li key={i.id}>
                    <b>{i.title}</b>
                    <ManuscriptOwnership item={i} provider={adapter} />
                  </li>
                ))}
              </ul>
            ) : (
              <Select
                label="대상 고객사"
                value={target}
                disabled={fixedTarget || busy}
                onChange={(e) =>
                  setTargetOverride({
                    key: selectionKey,
                    value: e.target.value,
                  })
                }
              >
                <option value="">선택하세요</option>
                {customers
                  .filter((c) => c.status !== "suspended")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Select>
            )}
            {fixedTarget && (
              <p className="ml-caption">
                고객사 원고는 자기 고객사로 대상이 고정됩니다.
              </p>
            )}
            {missingTarget && (
              <p className="ml-caption">
                대상 고객사를 선택해야 보낼 수 있습니다.
                {mixedTargets
                  ? " 대상이 없는 원고는 따로 선택해 보내세요."
                  : ""}
              </p>
            )}
            <div className="ml-form-grid">
              <Select
                label="실행 고객사"
                value={execution}
                onChange={(e) => {
                  setExecution(e.target.value);
                  onExecutionChange?.(e.target.value);
                  setMachine("auto");
                  setDevice("auto");
                }}
              >
                {customers
                  .filter((c) => c.status !== "suspended")
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ·{" "}
                      {executionAvailabilityLabel(availability.data?.[c.id])}
                      {!target || mixedTargets
                        ? ""
                        : c.id === target
                          ? " · 대상과 동일"
                          : " · 크루 실행"}
                    </option>
                  ))}
              </Select>
              <Select
                label="머신"
                disabled={
                  busy || machineData.loading || !!executionAvailability?.error
                }
                value={machine}
                onChange={(e) => {
                  setMachine(e.target.value);
                  setDevice("auto");
                }}
              >
                <option value="auto">자동 배정</option>
                {machineData.data?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
              <Select
                label="디바이스"
                disabled={busy || deviceData.loading || !!deviceData.error}
                value={device}
                onChange={(e) => setDevice(e.target.value)}
              >
                <option value="auto">자동 배정</option>
                {deviceData.data?.map((d) => (
                  <option key={d.serial} value={d.serial}>
                    {d.label}
                  </option>
                ))}
              </Select>
            </div>
            <p className="ml-caption">
              대상 폴더를 바꿔도 실행 고객사는 유지됩니다.
            </p>
            <ExecutionAvailabilityNotice
              result={executionAvailability}
              onRetry={availability.retry}
            />
            <AiModelSelect
              models={modelsData.data}
              value={llmModel}
              onChange={setLlmModel}
              disabled={busy || modelsData.loading}
            />
            <SegmentedControl
              label="실행 시점"
              value={when}
              onChange={setWhen}
              options={[
                { value: "now", label: "지금" },
                { value: "later", label: "시작 시각 + 간격" },
              ]}
            />
            {when === "later" && (
              <div className="ml-form-grid">
                <Field
                  label="시작 시각 (한국 시간)"
                  type="datetime-local"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
                <Field
                  label="원고 사이 간격 (분)"
                  type="number"
                  min={1}
                  value={interval}
                  onChange={(e) => setInterval(+e.target.value)}
                />
              </div>
            )}
            <div className="ml-send-keywords">
              {selected.map((i, index) => (
                <div key={i.id}>
                  <div>
                    <b>
                      {index + 1}. {i.title}
                    </b>
                    <small>
                      {when === "later" && start
                        ? new Date(
                            new Date(start + "+09:00").getTime() +
                              interval * 60000 * index,
                          ).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })
                        : "지금 실행"}
                    </small>
                  </div>
                  <Field
                    label={i.title + " · SEO 키워드"}
                    value={(keywords[i.id] ?? i.keywords).join(", ")}
                    onChange={(e) =>
                      setKeywords((k) => ({
                        ...k,
                        [i.id]: e.target.value
                          .split(",")
                          .map((x) => x.trim())
                          .filter(Boolean),
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <p className="ml-caption">
              원고 추천 키워드를 채웠습니다. 비공개·이웃 공개 원고는 SEO
              추적에서 제외됩니다. 같은 원고의 반복 게시 예약은 제공하지
              않습니다.
            </p>
          </>
        )}
        {results && (
          <>
            <div className="ml-owner">
              <Check size={20} />
              <strong>
                {results.filter((r) => r.ok).length}편 등록 ·{" "}
                {results.filter((r) => !r.ok).length}편 건너뜀
              </strong>
            </div>
            <div className="ml-result-list">
              {results.map((r) => (
                <article key={r.id}>
                  <Badge tone={r.ok ? "teal" : "amber"}>
                    {r.ok ? "등록됨" : "건너뜀"}
                  </Badge>
                  <b>{r.title}</b>
                  <p>{r.error ?? "작업 등록됨"}</p>
                  <small>
                    {r.ok ? "작업 등록됨" : "작업 미생성"} ·{" "}
                    {r.runAt
                      ? new Date(r.runAt).toLocaleString("ko-KR", {
                          timeZone: "Asia/Seoul",
                        })
                      : "지금"}
                    {r.seoSkipped && <span>{r.seoSkipped}</span>}
                  </small>
                </article>
              ))}
            </div>
            <p className="ml-caption">
              등록된 원고는 발행 예정으로 잠겼습니다. 건너뛴 원고와 기존 발행
              이력은 그대로 유지됩니다.
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
type UploadRow = {
  path: string;
  body: string;
  files: FileRef[];
  check?: ManuscriptCheck;
  reason?: string;
  metadataWarning?: string;
  keywords?: string[];
  memo?: string;
  valid: boolean;
};
export function LibraryUpload({
  provider,
  refine,
  initialChannel = "complete",
  initialFiles,
  refineExample,
  targets,
  ...props
}: {
  provider?: LibraryAdapter;
  refine?: RefineUploadProvider;
  initialChannel?: UploadChannel;
  initialFiles?: File[];
  refineExample?: RefineUploadExample;
  targets?: {
    customers: { id: string; name: string }[];
    folders: { id: string; customerId: string; name: string }[];
  };
  customerId: string;
  folderId: string | null;
  onClose: () => void;
  example?: boolean;
}) {
  const [channel, setChannel] = useState<UploadChannel>(initialChannel),
    [files, setFiles] = useState(initialFiles);
  const [target, setTarget] = useState({
    customerId: props.customerId,
    folderId: props.folderId,
  });
  const header = (busy: boolean) => (
    <UploadChannels
      channel={channel}
      onChange={setChannel}
      disabled={busy}
      completeAvailable={!!provider}
    />
  );
  return channel === "refine" || !provider ? (
    <RefineBatchUpload
      {...props}
      {...target}
      onTargetChange={setTarget}
      targetLabel={
        provider?.customers.find((c) => c.id === target.customerId)?.name
      }
      channelHeader={header}
      onSelection={setFiles}
      initialFiles={files}
      example={refineExample}
      targets={targets}
      refine={
        refine ?? {
          guidelines: [],
          canCreate: false,
          error: "정형화 업로드 연결을 준비하고 있습니다.",
        }
      }
    />
  ) : (
    <CompletedLibraryUpload
      {...props}
      {...target}
      provider={provider}
      initialFiles={files}
      channelHeader={header}
      onSelection={setFiles}
    />
  );
}
function CompletedLibraryUpload({
  channelHeader,
  onSelection,
  onClose,
  customerId,
  folderId,
  example = false,
  initialFiles,
  provider,
}: {
  onClose: () => void;
  customerId: string;
  folderId: string | null;
  example?: boolean;
  initialFiles?: File[];
  provider: LibraryAdapter;
  channelHeader?: (busy: boolean) => ReactNode;
  onSelection?: (files: File[]) => void;
}) {
  const adapter = provider,
    customerName = (id: string) => nameOf(adapter, id);
  const [keep, setKeep] = useState(true),
    [rows, setRows] = useState<UploadRow[]>([]),
    [saved, setSaved] = useState(false),
    [mode, setMode] = useState(
      example || initialFiles?.some((f) => f.webkitRelativePath.includes("/"))
        ? "folder"
        : "file",
    ),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [results, setResults] = useState<BatchResult[]>([]),
    [completed, setCompleted] = useState<string[]>([]);
  useEffect(() => {
    if (!example) return;
    let live = true;
    void (async () => {
      try {
        const inputs = [
          {
            path: "10월/검진",
            body: "# 건강검진 준비\n\n방문 준비 사항을 안내합니다.",
          },
          { path: "10월/진료", body: "# 진료 안내\n\n![사진](missing.jpg)" },
        ];
        const values = await Promise.all(
          inputs.map(async (i) => {
            const check = await adapter.check(customerId, i.body, []);
            return { ...i, files: [], check, valid: check.valid };
          }),
        );
        if (live)
          setRows([
            ...values,
            {
              path: "10월/보류",
              body: "",
              files: [],
              valid: false,
              reason: ".md 파일이 2개여서 건너뜀",
            },
          ]);
      } catch (e) {
        if (live) setError(libraryError(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [adapter, customerId, example]);
  const read = async (files: File[]) => {
    dropRead.current = true;
    onSelection?.(files);
    setError("");
    setSaved(false);
    setBusy(true);
    setCompleted([]);
    setResults([]);
    const groups = new Map<string, File[]>();
    files.forEach((f) => {
      const path =
        mode === "folder"
          ? f.webkitRelativePath.split("/").slice(0, -1).join("/") ||
            "폴더 밖 원고"
          : "파일 올리기";
      groups.set(path, [...(groups.get(path) ?? []), f]);
    });
    const result: UploadRow[] = [];
    try {
      for (const [path, list] of groups) {
        const md = list.filter((f) => /\.(md|txt)$/i.test(f.name));
        if (md.length !== 1) {
          result.push({
            path,
            body: "",
            files: [],
            valid: false,
            reason: md.length
              ? "원고 파일이 여러 개여서 건너뜀"
              : "원고 파일 없음 · 건너뜀",
          });
          continue;
        }
        let body = "";
        const refs: FileRef[] = [];
        let checked: ManuscriptCheck;
        try {
          body = await md[0].text();
          for (const file of list.filter((f) =>
            /\.(png|jpe?g|gif|webp|heic|mp4|mov|m4v|avi|mkv|webm|3gp)$/i.test(
              f.name,
            ),
          ))
            refs.push(await adapter.uploadFile(customerId, file));
          checked = await adapter.check(customerId, body, refs);
        } catch (e) {
          result.push({
            path,
            body,
            files: refs,
            valid: false,
            reason: libraryError(e),
          });
          continue;
        }
        let keywords: string[] = [],
          memo = "",
          metadataWarning = "";
        const info = list.find((f) => f.name === "등록정보.json");
        if (info) {
          try {
            const parsed = JSON.parse(await info.text());
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
              throw new Error();
            if (
              parsed.seoKeywords !== undefined &&
              (!Array.isArray(parsed.seoKeywords) ||
                !parsed.seoKeywords.every(
                  (k: unknown) => typeof k === "string",
                ))
            )
              throw new Error();
            if (parsed.memo !== undefined && typeof parsed.memo !== "string")
              throw new Error();
            keywords = (parsed.seoKeywords ?? [])
              .map((k: string) => k.trim())
              .filter(Boolean);
            memo = parsed.memo ?? "";
          } catch {
            metadataWarning =
              "등록정보.json 형식을 읽지 못했습니다. 원고는 저장되며 키워드·메모를 직접 입력해 주세요.";
          }
        }
        result.push({
          path,
          body,
          keywords,
          memo,
          metadataWarning,
          files: refs,
          check: checked,
          valid: checked.valid,
        });
      }
      if (result.filter((r) => !r.reason).length > 100) {
        setError("한 번에 최대 100편까지 올릴 수 있습니다.");
        setRows([]);
        return;
      }
      setRows(result);
    } catch (e) {
      setError(libraryError(e));
    } finally {
      setBusy(false);
    }
  };
  const dropRead = useRef(false);
  useEffect(() => {
    if (initialFiles?.length && !dropRead.current) {
      dropRead.current = true;
      void read(initialFiles);
    }
    // Initial files are a single captured drop; uploads are not repeated on rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles]);
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const pending = rows.filter(
      (r) => !r.reason && !completed.includes(r.path),
    );
    try {
      const response = await adapter.batch(
        customerId,
        folderId,
        pending.map((r) => ({
          path: keep && mode === "folder" ? r.path : undefined,
          body: r.body,
          files: r.files,
          keywords: r.keywords ?? [],
          memo: r.memo,
        })),
      );
      setResults(response);
      const done = [
        ...completed,
        ...pending.filter((_, i) => response[i]?.ok).map((r) => r.path),
      ];
      setCompleted(done);
      setSaved(
        rows.filter((r) => !r.reason).every((r) => done.includes(r.path)),
      );
      invalidateLibrary(adapter);
    } catch (e) {
      setError(libraryError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
      title="원고 올리기"
      description={
        customerName(customerId) +
        "의 원고함에 저장합니다. 형식 오류가 있는 원고도 작성 중으로 보관됩니다."
      }
      size="wide"
      footer={
        <DialogActions
          secondary={{
            label: saved ? "닫기" : "취소",
            onClick: onClose,
            disabled: busy,
          }}
          primary={
            !saved
              ? {
                  label:
                    rows.filter((r) => !r.reason && !completed.includes(r.path))
                      .length + "편 저장",
                  disabled:
                    busy ||
                    !rows.some((r) => !r.reason && !completed.includes(r.path)),
                  onClick: () => void save(),
                }
              : undefined
          }
        />
      }
    >
      <div className="ml-upload">
        {channelHeader?.(busy)}
        <SegmentedControl
          label="올리기 방법"
          value={mode}
          onChange={(v) => {
            if (busy) return;
            setMode(v);
            setCompleted([]);
            setResults([]);
            setSaved(false);
            setRows([]);
          }}
          options={[
            { value: "file", label: "파일 올리기" },
            { value: "folder", label: "폴더 올리기" },
          ]}
        />
        <div
          className="ml-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (busy) return;
            if (mode === "file") {
              void read(Array.from(e.dataTransfer.files));
              return;
            }
            const entries = Array.from(e.dataTransfer.items)
              .map((i) => i.webkitGetAsEntry())
              .filter((x): x is FileSystemEntry => !!x);
            const walk = async (
              entry: FileSystemEntry,
              prefix = "",
            ): Promise<File[]> => {
              const path = prefix + entry.name;
              if (entry.isFile)
                return new Promise((resolve, reject) =>
                  (entry as FileSystemFileEntry).file((file) => {
                    Object.defineProperty(file, "webkitRelativePath", {
                      value: path,
                    });
                    resolve([file]);
                  }, reject),
                );
              const reader = (entry as FileSystemDirectoryEntry).createReader();
              const children: FileSystemEntry[] = [];
              while (true) {
                const batch = await new Promise<FileSystemEntry[]>(
                  (resolve, reject) => reader.readEntries(resolve, reject),
                );
                if (!batch.length) break;
                children.push(...batch);
              }
              return (
                await Promise.all(
                  children.map((child) => walk(child, path + "/")),
                )
              ).flat();
            };
            if (!entries.length) {
              setError("이 브라우저에서는 아래 폴더 선택을 사용해 주세요.");
              return;
            }
            void Promise.all(entries.map((entry) => walk(entry)))
              .then((groups) => read(groups.flat()))
              .catch(() =>
                setError(
                  "폴더를 읽지 못했습니다. 아래 폴더 선택으로 다시 시도해 주세요.",
                ),
              );
          }}
        >
          <Upload size={28} />
          <b>
            {mode === "folder"
              ? "원고 폴더를 놓거나 선택하세요"
              : "원고 1개와 사진·영상을 놓으세요"}
          </b>
          <p>
            {mode === "folder"
              ? ".md가 있는 폴더마다 원고 1편 · 최대 100편"
              : "원고 .md 또는 .txt 1개 + 사진·영상"}
          </p>
          <input
            aria-label={
              mode === "folder" ? "업로드 폴더 선택" : "업로드 파일 선택"
            }
            disabled={busy}
            type="file"
            multiple
            {...(mode === "folder"
              ? ({
                  webkitdirectory: "",
                  directory: "",
                } as InputHTMLAttributes<HTMLInputElement>)
              : { accept: ".md,.txt,image/*,video/*" })}
            onChange={(e) => void read(Array.from(e.target.files ?? []))}
          />
        </div>
        {mode === "folder" && (
          <Checkbox
            label="하위 폴더 구조 유지"
            checked={keep}
            onChange={setKeep}
          />
        )}
        {error && (
          <p className="ml-error" role="alert">
            {error}
          </p>
        )}
        {busy && <p role="status">파일 처리 중</p>}
        {saved && <p role="status">원고함에 저장했습니다.</p>}
        {results.map((r, i) => (
          <p
            key={i}
            role={r.ok ? "status" : "alert"}
            className={r.ok ? "ml-notice" : "ml-error"}
          >
            {r.title ?? r.path} · {r.ok ? "저장됨" : r.error}
            {r.errors?.join(" · ")}
            {!!r.sameBody &&
              " · 같은 대상 고객사에 내용이 같은 원고가 있습니다 (" +
                r.sameBody +
                "편)"}
          </p>
        ))}
        <div className="ml-result-list">
          {rows.map((r, i) => (
            <article key={i}>
              <b>{r.path}</b>
              <Badge tone={r.reason ? "neutral" : r.valid ? "teal" : "amber"}>
                {r.reason
                  ? "건너뜀"
                  : r.valid
                    ? "검사 통과 · 준비됨"
                    : "검사 오류 · 작성 중"}
              </Badge>
              <p>
                {r.reason ??
                  (r.check?.errors.join(" · ") || "첨부 이름 대조 완료")}
              </p>
              <small>
                {r.files.length}개 첨부 · 오류 {r.check?.errors.length ?? 0}개 ·
                경고 {r.metadataWarning ? 1 : 0}개
              </small>
              {!!r.keywords?.length && (
                <p>추천 키워드 · {r.keywords.join(", ")}</p>
              )}
              {r.memo && <p>메모 · {r.memo}</p>}
              {r.metadataWarning && (
                <p className="ml-warning">{r.metadataWarning}</p>
              )}
            </article>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
function LibraryDocument({
  onOpenRefine,
  documentId,
  documentEdit = false,
  customerId: customer,
  canWrite = true,
  canSend = true,
  example = "normal",
  onDirtyChange,
  provider: adapter,
}: {
  onOpenRefine?: (id: string) => void;
  documentId: string;
  documentEdit?: boolean;
  customerId: string;
  canWrite?: boolean;
  canSend?: boolean;
  example?: LibraryExample;
  onDirtyChange?: (dirty: boolean) => void;
  provider: LibraryAdapter;
}) {
  const customerName = (id: string) => nameOf(adapter, id),
    selectedId = documentId,
    allowWrite = canWrite && example !== "readonly";
  const [busy, setBusy] = useState(false),
    [uploadBusy, setUploadBusy] = useState(false),
    [dirty, setDirty] = useState(example === "conflict");
  const busyRef = useRef(false),
    hydrated = useRef("");
  const [editing, setEditing] = useState(
    canWrite &&
      (documentEdit || example === "editor" || example === "conflict"),
  );
  const [body, setBody] = useState(""),
    [files, setFiles] = useState<FileRef[]>([]),
    [editRevision, setEditRevision] = useState(0);
  const [tab, setTab] = useState(
    example === "files"
      ? "파일"
      : example === "versions"
        ? "버전"
        : example === "history"
          ? "사용 이력"
          : "원고",
  );
  const [error, setError] = useState(
      example === "conflict"
        ? "다른 관리자이 먼저 수정했습니다. 내 초안은 유지됩니다. 최신 버전을 확인해 주세요."
        : "",
    ),
    [note, setNote] = useState("");
  const [compare, setCompare] = useState(false),
    [leftVersion, setLeftVersion] = useState(1),
    [rightVersion, setRightVersion] = useState(2);
  const [pending, setPending] = useState<(() => void) | null>(null),
    [folderAction, setFolderAction] = useState("");
  const [sendOpen, setSendOpen] = useState(false),
    [sendIds, setSendIds] = useState<string[]>([]);
  const detail = useLibraryResource(adapter, "detail:" + selectedId, () =>
    adapter.detail(selectedId),
  );
  const item = detail.data ?? undefined;
  const write = allowWrite && !item?.readOnly && !item?.ownerCustomerId;
  const duplicates = item?.sameBody ?? [];
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (item && hydrated.current !== item.id) {
      hydrated.current = item.id;
      setBody(item.body);
      setFiles(item.files);
      setEditRevision(item.revision);
      setLeftVersion(item.revisions.at(-1)?.revision ?? item.revision);
      setRightVersion(item.revision);
    }
  }, [item]);
  const savedCheck = useManuscriptCheck(
    adapter,
    customer,
    item?.body ?? "",
    item?.files ?? [],
    tab === "파일" && !!item,
  );
  const currentCheck = useManuscriptCheck(
    adapter,
    customer,
    body,
    files,
    editing && write,
  );
  const oldRevision = useLibraryResource(
    adapter,
    compare && item ? "revision:" + item.id + ":" + leftVersion : null,
    () => adapter.revision(selectedId, leftVersion),
  );
  const newRevision = useLibraryResource(
    adapter,
    compare && item ? "revision:" + item.id + ":" + rightVersion : null,
    () => adapter.revision(selectedId, rightVersion),
  );
  const run = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
      invalidateLibrary(adapter);
    } catch (e) {
      setError(libraryError(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const navigate = (action: () => void) => {
    if (busyRef.current || uploadBusy) return;
    if (dirty) setPending(() => action);
    else action();
  };
  const save = () =>
    run(async () => {
      if (!write || !item || item.locked || uploadBusy) return;
      const result = await adapter.revise(item.id, {
        body,
        files,
        revision: editRevision,
      });
      setDirty(false);
      setEditing(false);
      hydrated.current = "";
      setNote(
        result.changed
          ? "새 버전 v" + result.item.revision + "로 저장했습니다."
          : "내용이 같아 버전을 유지했습니다.",
      );
    });
  return (
    <div
      className={"ml-screen ml-document-only " + (editing ? "is-editing" : "")}
    >
      {detail.error && (
        <p role="alert" className="ml-error">
          {detail.error}
          <Button onClick={detail.retry}>다시 읽기</Button>
        </p>
      )}
      {note && (
        <p role="status" className="ml-notice">
          {note}
        </p>
      )}
      {error && !editing && !folderAction && (
        <p role="alert" className="ml-error">
          {error}
        </p>
      )}
      <section className="ml-detail" tabIndex={-1}>
        {item ? (
          <>
            <header className="ml-detail-head">
              <div>
                <span className="ml-caption">v{item.revision}</span>
                <h2>{item.title}</h2>
                <ManuscriptOwnership item={item} provider={adapter} />
              </div>
              <Status item={item} />
            </header>
            <SegmentedControl
              label="원고 상세 탭"
              value={tab}
              onChange={setTab}
              options={["원고", "파일", "버전", "사용 이력"].map((value) => ({
                value,
                label: value,
              }))}
            />
            {(item.readOnly || item.ownerCustomerId) && (
              <p className="ml-lock">
                고객사가 등록한 원고입니다. 직원은 읽기만 가능합니다.
              </p>
            )}
            {item.locked && (
              <p className="ml-lock">
                <LockKeyhole size={15} />
                {item.status === "published"
                  ? "게시된 원고는 영구 고정되어 수정·재발행할 수 없습니다."
                  : "발행 예정 원고입니다. 수정하려면 연결된 작업을 먼저 취소해 주세요."}
              </p>
            )}
            {duplicates.length > 0 && (
              <div className="ml-warning">
                <strong>같은 대상 고객사에 내용이 같은 원고가 있습니다</strong>
                {duplicates.map((d) => (
                  <p key={d.id}>
                    {d.visible === false ? (
                      "내용이 같은 원고가 이미 있습니다"
                    ) : (
                      <>
                        {d.customerName} · {d.title} · {libraryStatus[d.status]}
                      </>
                    )}
                    {d.status === "scheduled" || d.status === "published"
                      ? " — 보내기 불가"
                      : ""}
                  </p>
                ))}
              </div>
            )}
            {tab === "원고" &&
              (editing && write ? (
                <>
                  <div className="ml-editor">
                    <LibraryEditor
                      key={item.id}
                      provider={adapter}
                      customerId={customer}
                      onBusyChange={setUploadBusy}
                      value={body}
                      onChange={(v) => {
                        setBody(v);
                        setDirty(true);
                      }}
                      files={files}
                      onFiles={(v) => {
                        setFiles(v);
                        setDirty(true);
                      }}
                    />
                  </div>
                  {error && (
                    <p role="alert" className="ml-error">
                      {error}
                    </p>
                  )}
                  <DialogActions
                    secondary={{
                      label: "편집 취소",
                      onClick: () =>
                        navigate(() => {
                          setEditing(false);
                          setDirty(false);
                          setError("");
                        }),
                    }}
                    primary={{
                      disabled:
                        busy ||
                        uploadBusy ||
                        currentCheck.loading ||
                        !!currentCheck.error,
                      label:
                        currentCheck.data && !currentCheck.data.valid
                          ? "작성 중으로 저장"
                          : "새 버전 저장",
                      onClick: save,
                    }}
                  />
                  {error && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEditing(false);
                        detail.retry();
                        setTab("버전");
                        setError("");
                        setNote(
                          "내 초안은 유지됩니다. 원고 탭에서 편집을 다시 열면 이어 쓸 수 있습니다.",
                        );
                      }}
                    >
                      최신 버전 확인
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {item.refineOrigin && (
                    <Button
                      variant="ghost"
                      onClick={() => onOpenRefine?.(item.refineOrigin!.id)}
                      disabled={!onOpenRefine}
                    >
                      출처: 정형화 {item.refineOrigin.id} 시도{" "}
                      {item.refineOrigin.seq}
                    </Button>
                  )}
                  {!!item.notices?.length && (
                    <details>
                      <summary>검토 {item.notices.length}개</summary>
                      <ReviewNotices notices={item.notices} />
                    </details>
                  )}
                  <LibraryPreview
                    provider={adapter}
                    customerId={customer}
                    body={item.body}
                    files={item.files}
                  />
                  <div className="ml-detail-actions">
                    {write && !item.locked && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (!dirty) {
                            setBody(item.body);
                            setFiles(item.files);
                            setEditRevision(item.revision);
                          }
                          setEditing(true);
                        }}
                      >
                        편집
                      </Button>
                    )}
                    {canSend && item.status === "ready" && (
                      <Button
                        disabled={duplicates.some(
                          (d) =>
                            d.status === "scheduled" ||
                            d.status === "published",
                        )}
                        onClick={() => {
                          setSendIds([item.id]);
                          setSendOpen(true);
                        }}
                      >
                        <Send size={14} />
                        작업으로 보내기
                      </Button>
                    )}
                    {write && (
                      <Button
                        variant="ghost"
                        onClick={() =>
                          void run(async () => {
                            await adapter.patch(item.id, {
                              archived: item.status !== "archived",
                            });
                          })
                        }
                      >
                        <Archive size={14} />
                        {item.status === "archived" ? "되돌리기" : "보관"}
                      </Button>
                    )}
                    {write && !item.jobs && !item.locked && (
                      <Button
                        variant="ghost"
                        onClick={() => setFolderAction("delete-item")}
                      >
                        삭제
                      </Button>
                    )}
                    {item.jobs > 0 && (
                      <small>
                        사용 이력이 있는 원고는 삭제 대신 보관합니다.
                      </small>
                    )}
                  </div>
                  <Field
                    label="추천 SEO 키워드"
                    key={item.id + item.keywords.join(",")}
                    defaultValue={item.keywords.join(", ")}
                    readOnly={!write || item.locked}
                    disabled={busy}
                    onBlur={(e) => {
                      const keywords = e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean);
                      if (
                        JSON.stringify(keywords) !==
                        JSON.stringify(item.keywords)
                      )
                        void run(async () => {
                          await adapter.patch(item.id, { keywords });
                        });
                    }}
                  />
                  <Field
                    label="메모"
                    key={item.id + item.memo}
                    defaultValue={item.memo ?? ""}
                    maxLength={500}
                    readOnly={!write}
                    disabled={busy}
                    onBlur={(e) => {
                      const memo = e.target.value;
                      if (memo !== (item.memo ?? ""))
                        void run(async () => {
                          await adapter.patch(item.id, { memo });
                        });
                    }}
                  />
                </>
              ))}
            {tab === "파일" && (
              <div className="ml-files">
                {item.files.length ? (
                  item.files.map((f) => (
                    <article key={f.fileId}>
                      <LibraryMedia
                        provider={adapter}
                        customerId={customer}
                        file={f.fileId}
                        name={f.name}
                        video={
                          savedCheck.data?.media.find(
                            (m) => m.fileId === f.fileId,
                          )?.video
                        }
                      />
                      <b>{f.name}</b>
                      {savedCheck.data && (
                        <Badge
                          tone={
                            savedCheck.data.media.some(
                              (m) => m.fileId === f.fileId,
                            )
                              ? "teal"
                              : "amber"
                          }
                        >
                          {savedCheck.data.media.some(
                            (m) => m.fileId === f.fileId,
                          )
                            ? "본문과 일치"
                            : "본문에 없음"}
                        </Badge>
                      )}
                    </article>
                  ))
                ) : (
                  <p className="ml-empty">첨부 파일이 없습니다.</p>
                )}
                {savedCheck.error && (
                  <p role="alert" className="ml-error">
                    {savedCheck.error}
                  </p>
                )}
              </div>
            )}
            {tab === "버전" && (
              <div className="ml-versions">
                <div className="ml-form-grid">
                  <Select
                    label="비교할 이전 버전"
                    value={leftVersion}
                    onChange={(e) => setLeftVersion(+e.target.value)}
                  >
                    {item.revisions.map((v) => (
                      <option key={v.revision} value={v.revision}>
                        v{v.revision}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="비교할 이후 버전"
                    value={rightVersion}
                    onChange={(e) => setRightVersion(+e.target.value)}
                  >
                    {item.revisions.map((v) => (
                      <option key={v.revision} value={v.revision}>
                        v{v.revision}
                      </option>
                    ))}
                  </Select>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setCompare(!compare)}
                >
                  {compare ? "비교 닫기" : "두 버전 비교"}
                </Button>
                {[oldRevision.error, newRevision.error]
                  .filter(Boolean)
                  .map((e, i) => (
                    <p role="alert" className="ml-error" key={i}>
                      {e}
                    </p>
                  ))}
                {(oldRevision.error || newRevision.error) && (
                  <Button
                    onClick={() => {
                      oldRevision.retry();
                      newRevision.retry();
                    }}
                  >
                    버전 다시 읽기
                  </Button>
                )}
                {compare && (
                  <div className="ml-compare">
                    {[leftVersion, rightVersion].map((rev, index) => (
                      <article key={index}>
                        <h3>v{rev}</h3>
                        <pre>
                          {(index === 0 ? oldRevision : newRevision).data
                            ?.body ?? "버전 읽는 중"}
                        </pre>
                      </article>
                    ))}
                  </div>
                )}
                {item.revisions.map((v) => (
                  <article className="ml-revision" key={v.revision}>
                    <b>v{v.revision}</b>
                    <span>
                      {v.createdAt} · {v.createdByName ?? "운영팀"}
                    </span>
                    <Badge tone={v.valid ? "teal" : "amber"}>
                      {v.valid ? "검사 통과" : "형식 오류"}
                    </Badge>
                  </article>
                ))}
              </div>
            )}
            {tab === "사용 이력" && (
              <div className="ml-history">
                {item.uses.length ? (
                  item.uses.map((u, index) => (
                    <article key={index}>
                      <header>
                        <b>{u.jobId}</b>
                        <Badge>{u.runStatus}</Badge>
                      </header>
                      <p>
                        실행 · {customerName(u.executeCustomerId)}
                        {u.executeCustomerId !== item.customerId
                          ? " · 크루 실행"
                          : ""}
                      </p>
                      <p>
                        원고 v{u.revision} · {u.runAt}
                      </p>
                      <p>결과 검토 · {u.reviewStatus}</p>
                      {u.postUrl && (
                        <a href={u.postUrl} target="_blank" rel="noreferrer">
                          게시 글 열기 ↗
                        </a>
                      )}
                      <div className="ml-keywords">
                        {u.keywords.map((k) => (
                          <Badge key={k}>{k}</Badge>
                        ))}
                      </div>
                      <small>
                        SEO 성과는 대상 고객사{" "}
                        {manuscriptTarget(item)
                          ? customerName(manuscriptTarget(item)!)
                          : "미지정"}{" "}
                        기준
                      </small>
                    </article>
                  ))
                ) : (
                  <p className="ml-empty">
                    아직 작업으로 보낸 이력이 없습니다.
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="ml-empty">
            <FileText size={30} />
            <b>원고를 선택해 주세요</b>
            <span>본문·파일·버전·사용 이력을 살펴볼 수 있습니다.</span>
          </div>
        )}
      </section>
      {sendOpen && canSend && (
        <LibrarySendDialog
          provider={adapter}
          open
          initialIds={sendIds}
          initialCustomer={customer}
          onClose={() => setSendOpen(false)}
        />
      )}
      <Dialog
        open={!!folderAction}
        title="사용하지 않은 원고 삭제"
        onOpenChange={(v) => {
          if (!v && !busy) setFolderAction("");
        }}
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setFolderAction("") }}
            primary={{
              label: "삭제",
              tone: "danger",
              disabled: busy,
              onClick: () =>
                void run(async () => {
                  if (item && !item.locked && !item.jobs) {
                    await adapter.remove(item.id);
                    setFolderAction("");
                    setNote("원고를 삭제했습니다.");
                  }
                }),
            }}
          />
        }
      >
        {error && (
          <p role="alert" className="ml-error">
            {error}
          </p>
        )}
        작업에 사용하지 않은 원고를 삭제합니다.
      </Dialog>
      <Dialog
        open={!!pending}
        title="저장하지 않은 원고"
        description="편집한 내용을 저장하거나 버린 뒤 이동해 주세요."
        onOpenChange={(v) => {
          if (!v) setPending(null);
        }}
        footer={
          <DialogActions
            secondary={{ label: "계속 편집", onClick: () => setPending(null) }}
            primary={{
              label: "변경 버리고 이동",
              onClick: () => {
                setDirty(false);
                setPending(null);
                pending?.();
              },
            }}
          />
        }
      >
        {null}
      </Dialog>
    </div>
  );
}
export function ManuscriptLibrary(props: LibraryExplorerProps) {
  return (
    <LibraryExplorer
      {...props}
      renderDocument={(p) => (
        <LibraryDocument
          key={p.id}
          onOpenRefine={props.onOpenRefine}
          provider={p.provider}
          customerId={p.customerId}
          documentId={p.id}
          documentEdit={p.edit}
          canWrite={props.canWrite}
          canSend={props.canSend}
          example={props.example}
          onDirtyChange={p.onDirtyChange}
        />
      )}
      renderUpload={(p) => (
        <LibraryUpload
          provider={p.provider}
          customerId={p.customerId}
          folderId={p.folderId}
          initialFiles={p.files}
          initialChannel={p.channel}
          refine={props.refineUpload}
          example={props.example === "batch"}
          onClose={p.onClose}
        />
      )}
      renderSend={(p) => (
        <LibrarySendDialog
          provider={p.provider}
          open
          initialCustomer={p.customerId}
          initialIds={p.ids}
          initialStep={p.step}
          onClose={p.onClose}
        />
      )}
    />
  );
}
